/**
 * Emotion Analysis Tab
 * Real-time emotion monitoring for therapy sessions
 * Displays participant emotion cards with live updates
 */
import React from 'react';
import { makeStyles } from 'tss-react/mui';
// Emotion colors matching the original dashboard
const EMOTION_COLORS = {
    happy: '#4CAF50',
    sad: '#2196F3',
    angry: '#F44336',
    fearful: '#9C27B0',
    surprised: '#FF9800',
    disgusted: '#795548',
    neutral: '#9E9E9E'
};
const useStyles = makeStyles()((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        height: '100%'
    },
    connectionStatus: {
        padding: '12px 16px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '13px',
        fontWeight: 500
    },
    connected: {
        background: 'rgba(76, 175, 80, 0.15)',
        color: '#4CAF50',
        border: '1px solid rgba(76, 175, 80, 0.3)'
    },
    disconnected: {
        background: 'rgba(244, 67, 54, 0.15)',
        color: '#F44336',
        border: '1px solid rgba(244, 67, 54, 0.3)'
    },
    statusDot: {
        width: '10px',
        height: '10px',
        borderRadius: '50%',
        animation: '$pulse 2s infinite'
    },
    '@keyframes pulse': {
        '0%, 100%': {
            opacity: 1,
            transform: 'scale(1)'
        },
        '50%': {
            opacity: 0.6,
            transform: 'scale(1.2)'
        }
    },
    emptyState: {
        textAlign: 'center',
        padding: '40px 20px',
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: '14px'
    },
    participantList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        overflowY: 'auto',
        maxHeight: '500px',
        paddingRight: '8px',
        '&::-webkit-scrollbar': {
            width: '6px'
        },
        '&::-webkit-scrollbar-track': {
            background: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '3px'
        },
        '&::-webkit-scrollbar-thumb': {
            background: 'rgba(11, 108, 227, 0.4)',
            borderRadius: '3px',
            '&:hover': {
                background: 'rgba(11, 108, 227, 0.6)'
            }
        }
    },
    participantCard: {
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.05) 100%)',
        borderRadius: '12px',
        padding: '16px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
            borderColor: 'rgba(30, 136, 229, 0.4)',
            background: 'linear-gradient(135deg, rgba(30, 136, 229, 0.15) 0%, rgba(11, 108, 227, 0.1) 100%)',
            transform: 'translateX(4px)'
        }
    },
    cardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '12px'
    },
    participantName: {
        fontSize: '15px',
        fontWeight: 600,
        color: '#FFFFFF'
    },
    dominantEmotion: {
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'capitalize',
        display: 'inline-block'
    },
    emotionBars: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        marginTop: '12px'
    },
    emotionRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    emotionLabel: {
        width: '70px',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.7)',
        textTransform: 'capitalize'
    },
    emotionBarContainer: {
        flex: 1,
        height: '6px',
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '3px',
        overflow: 'hidden',
        position: 'relative'
    },
    emotionBarFill: {
        height: '100%',
        borderRadius: '3px',
        transition: 'width 0.5s ease',
        position: 'relative',
        '&::after': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%)',
            animation: '$shimmer 2s infinite'
        }
    },
    '@keyframes shimmer': {
        '0%': {
            transform: 'translateX(-100%)'
        },
        '100%': {
            transform: 'translateX(100%)'
        }
    },
    emotionValue: {
        width: '40px',
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.9)',
        textAlign: 'right',
        fontWeight: 500
    },
    engagementMeter: {
        marginTop: '12px',
        padding: '10px',
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    engagementLabel: {
        fontSize: '12px',
        color: 'rgba(255, 255, 255, 0.7)'
    },
    engagementValue: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#4CAF50'
    },
    latency: {
        fontSize: '11px',
        color: 'rgba(255, 255, 255, 0.5)',
        marginTop: '8px',
        textAlign: 'right'
    }
}));
export default function EmotionAnalysisTab({ connected, participants, error }) {
    const { classes, cx } = useStyles();
    const participantArray = Array.from(participants.values());
    const getEmotionColor = (emotion) => {
        return EMOTION_COLORS[emotion] || EMOTION_COLORS.neutral;
    };
    return (React.createElement("div", { className: classes.container, "data-testid": "emotion-analysis-tab" },
        React.createElement("div", { className: cx(classes.connectionStatus, connected ? classes.connected : classes.disconnected) },
            React.createElement("div", { className: classes.statusDot, style: { background: connected ? '#4CAF50' : '#F44336' } }),
            React.createElement("span", null, connected ? 'Connected' : 'Disconnected'),
            error && React.createElement("span", { style: { marginLeft: 'auto', fontSize: '11px' } },
                "(",
                error,
                ")")),
        participantArray.length === 0 ? (React.createElement("div", { className: classes.emptyState }, connected ? 'No participants yet' : 'Connecting to emotion detection service...')) : (React.createElement("div", { className: classes.participantList }, participantArray.map(participant => (React.createElement("div", { key: participant.id, className: classes.participantCard, "data-testid": "participant-emotion-card" },
            React.createElement("div", { className: classes.cardHeader },
                React.createElement("span", { className: classes.participantName }, participant.name),
                participant.currentEmotions && (React.createElement("span", { className: classes.dominantEmotion, style: {
                        background: getEmotionColor(participant.currentEmotions.dominant),
                        color: '#FFFFFF'
                    } }, participant.currentEmotions.dominant))),
            participant.currentEmotions ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: classes.emotionBars }, Object.entries(participant.currentEmotions.all).map(([emotion, value]) => (React.createElement("div", { key: emotion, className: classes.emotionRow },
                    React.createElement("span", { className: classes.emotionLabel }, emotion),
                    React.createElement("div", { className: classes.emotionBarContainer },
                        React.createElement("div", { className: classes.emotionBarFill, style: {
                                width: `${value}%`,
                                background: getEmotionColor(emotion)
                            } })),
                    React.createElement("span", { className: classes.emotionValue },
                        Math.round(value),
                        "%"))))),
                React.createElement("div", { className: classes.engagementMeter },
                    React.createElement("span", { className: classes.engagementLabel }, "Engagement"),
                    React.createElement("span", { className: classes.engagementValue },
                        Math.round(participant.currentEmotions.engagement),
                        "%")),
                participant.latency > 0 && (React.createElement("div", { className: classes.latency },
                    "Processing: ",
                    participant.latency,
                    "ms")))) : (React.createElement("div", { className: classes.emptyState, style: { padding: '20px' } }, "Waiting for emotion data...")))))))));
}
